import React from 'react'
import Delta from 'quill-delta'
import { FileUploadRow, FileUploadRowHandle, UploadedFile } from '../../../components/media/fileUpload'
import { ConversationData, ReplyOption, ReplyOptionTitle } from './conversation'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { IconFileUpload, IconPhotoUpload, IconTimes } from '../../../icons/library'
import { getNormalTagsForUser, isDeltaEmpty, QuillTextarea } from '../../../components/quill/quillTextarea'
import { useAppContext } from '../../../providers/context'
import { prepareDeltaToPost, wrapSetState } from '../../../utils'
import { LabeledCheckbox } from '../../../components/form/checkbox'
import { usePreview } from '../../modals/preview'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { largeMessageWarning } from '../compose/send'
import { DropTarget } from '../../../components/media/dropTarget'
import { useTranslation } from '../../../providers/translation'

export type ReplyState = {
  delta: Delta
  html?: string
  confirmReceipt: boolean
  attachments: UploadedFile[]
}

export const DEFAULT_REPLY_STATE: ReplyState = {
  delta: new Delta(),
  confirmReceipt: false,
  attachments: [],
}

export type ConversationReplyProps = {
  state: ReplyState
  setState: React.Dispatch<React.SetStateAction<ReplyState>>
  replyOption: ReplyOption
  conversation: ConversationData
  close: () => void
  onSend: () => void
}

export const ConversationReply: React.FC<ConversationReplyProps> = ({
  state,
  setState,
  replyOption,
  conversation,
  close,
  onSend,
}) => {
  const { state: appState } = useAppContext()
  const { tr } = useTranslation()
  const user = appState.user!

  const [loading, setLoading] = React.useState(false)
  const [currentlyUploading, setCurrentlyUploding] = React.useState(false)
  const { showMessagePreview } = usePreview()
  const tags = React.useMemo(() => getNormalTagsForUser(user), [user])
  const fileUploadHandleRef = React.useRef<FileUploadRowHandle>(null)

  const canSend = !isDeltaEmpty(state.delta) && !currentlyUploading

  return (
    <DropTarget
      canDrop={() => !!fileUploadHandleRef.current}
      onDrop={
        fileUploadHandleRef.current
          ? (files) => {
              fileUploadHandleRef.current!.upload(files)
            }
          : undefined
      }
      className={`m-2 flex max-h-[60vh] flex-col rounded-lg shadow-lg ${loading ? 'pointer-events-none *:opacity-50' : ''}`}
    >
      <div className="flex flex-row items-center justify-between rounded-t-lg border-b border-slate-200 bg-slate-50 py-1 pl-2 pr-1">
        <div>{conversation.messageForTags.subject && <>Re: {conversation.messageForTags.subject}</>}</div>
        <Button close onClick={close}>
          <IconTimes />
        </Button>
      </div>
      <QuillTextarea
        autofocus
        valueHTML={state.html}
        onValueChange={(html, delta) => setState((s) => ({ ...s, html, delta }))}
        tags={tags}
        caption={tr('select text for formatting options')}
        formats={['bold', 'italic', 'list', 'link']}
        flagPoorTranslations
        className="flex-grow !border-none"
        imageHandler={(file) => {
          if (fileUploadHandleRef.current) fileUploadHandleRef.current.upload([file])
        }}
        warnIfLong={['text', 'both', 'mass'].includes(conversation.type)}
        constrainTooltipWithinEditor
      />
      <div className="mb-2 flex flex-wrap justify-between gap-2 border-t border-slate-200 p-2">
        <div className="flex flex-grow flex-wrap justify-between gap-2">
          <FileUploadRow
            ref={fileUploadHandleRef}
            uploadedFiles={state.attachments}
            setUploadedFiles={wrapSetState(setState, 'attachments')}
            onUploading={setCurrentlyUploding}
          >
            <Button buttonStyle={ButtonStyle.ghostLight}>
              <IconFileUpload className="-sm:hidden" />
              <IconPhotoUpload className="sm:hidden" />
            </Button>
          </FileUploadRow>
          {user.type === 'teacher' && conversation.type !== 'mass' && (
            <LabeledCheckbox
              charcoal
              checked={state.confirmReceipt}
              onChecked={wrapSetState(setState, 'confirmReceipt')}
              label="Confirm Receipt"
              tooltip={
                <>
                  Get confirmation that each recipient opens your message. <b>Note:</b> This requires users to click a
                  link to view this message.
                </>
              }
            />
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {user.type === 'teacher' ? (
            <div
              className="flex cursor-pointer flex-wrap gap-1 px-2 font-semibold text-logo hover:underline"
              onClick={() => showMessagePreview(replyStateToPostableMessage(state, conversation, replyOption))}
            >
              <b>Preview</b>
              <div>
                <b>(</b>
                {tr('Notifying')} <b>{replyOption.recipientNotify}</b>, {tr('visible to')}{' '}
                <b>{replyOption.recipientCount}</b> {replyOption.recipientCount >= 1 ? tr('people') : tr('person')}
                <b>)</b>
              </div>
            </div>
          ) : (
            <div className="px-2 font-semibold text-logo">
              {tr('Notifying')} <b>{replyOption.recipientNotify}</b>, {tr('visible to')}{' '}
              <b>{replyOption.recipientCount}</b> {replyOption.recipientCount >= 1 ? tr('people') : tr('person')}
            </div>
          )}
          <Button
            logo
            disabled={!canSend}
            onClick={async () => {
              setLoading(true)
              if (
                !(await largeMessageWarning(
                  conversation.type === 'text' ||
                    conversation.type === 'both' ||
                    (!!conversation.massIconTypes && conversation.massIconTypes.includes('text')),
                  replyOption.recipientCount
                ))
              ) {
                setLoading(false)
                return
              }
              makeRequest<void>(
                '/frontend_api/sendReply',
                replyStateToPostableMessage(state, conversation, replyOption)
              )
                .then(onSend)
                .catch(showAxiosErrorAsToast)
                .finally(() => {
                  setLoading(false)
                })
            }}
          >
            <ReplyOptionTitle replyOption={replyOption} />
          </Button>
        </div>
      </div>
    </DropTarget>
  )
}

export type PostableReply = ReturnType<typeof replyStateToPostableMessage>

const replyStateToPostableMessage = (state: ReplyState, conversation: ConversationData, replyOption: ReplyOption) => {
  return {
    body: prepareDeltaToPost(state.delta),
    attachments: state.attachments.map((c) => c.fileName),
    message_id: conversation.id,
    replytype: replyOption.type,
    confirmReceipt: state.confirmReceipt,
  }
}
